import {
  createRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import useAPI from './useAPI.js';
import zoneContext from '../contexts/zone.js';
import scopeLogger from '../lib/scope-logger.js';
import { filter } from 'rxjs';

const console = scopeLogger('RMI:ext-ctrl-hook');

function getElementTypeAlias(tagName) {
  if (tagName === 'INPUT') {
    return 'input';
  }

  return 'button';
}

/**
 * Grab passed reference to DOM node or create new one, subscribe to position updates
 * and inform host app about them.
 * Listen to events on externals (remote) control and invoke local handlers.
 * Passed handlers override defaults.
 *
 * Default handlers:
 *
 *  - passing click to referenced node and add 'active' in dataset;
 *  - add 'hover' to element dataset on mouse enter;
 *  - remove 'hover' and 'active' on mouse leave;
 *
 *  Caveats: mouse down and leave element does not remove focus from element;
 *
 * @param {React.MutableRefObject<T>} [nodeRef] React ref. to node.
 * @param {{onInput?: function, onOver?: function, onLeave?: function, onClick?: function}} [handlers = {}] Handlers
 * @param {string[]} [attributes = []] Attributes
 * @return {React.MutableRefObject<T>} Passed or created React reference
 */
export function useExternalControl(nodeRef, handlers, attributes) {
  const API = useAPI();
  const {
    hostApp: { createControl, removeControl },
  } = API;

  const innerRef = useMemo(() => {
    return nodeRef ?? createRef();
  }, [nodeRef]);

  const hasReflection = useRef(false);
  const position = useRef(null);

  const { zone_id, registerElement } = useContext(zoneContext);

  const eventHandlers = useMemo(() => {
    /* TODO: At the moment, we create and pass to host all expected events,
         make a  sense to limit it only to required */
    return {
      /*
      // cause strange problem: does not handle tap on touchpad as click, just as mousedown,
      // but correct handle mousedown and click via mouse button
      mousedown:(() => {
        innerRef.current.dataset.active = 'active';
      }),
      onMouseDown: 'yes, while we can not rely on re-map event name somewhere else, need to pass "react" event name',
*/
      click:
        handlers?.onClick ??
        (() => {
          delete innerRef.current.dataset.hover;
          delete innerRef.current.dataset.focus;
          innerRef.current.dataset.active = 'active';
          innerRef.current.click();
        }),
      over:
        handlers?.onOver ??
        (() => {
          const hovered = document.querySelectorAll(
            '[data-hover="hover"],[data-focus="focus"]',
          );
          for (let i = 0; i < hovered.length; i++) {
            const hoveredElement = hovered[i];
            delete hoveredElement.dataset.hover;
            delete hoveredElement.dataset.focus;
          }
          innerRef.current.dataset.hover = 'hover';
          innerRef.current.dataset.focus = 'focus';
        }),
      leave:
        handlers?.onLeave ??
        (() => {
          delete innerRef.current.dataset.active;
          delete innerRef.current.dataset.hover;
          delete innerRef.current.dataset.focus;
        }),
      focus:
        handlers?.onFocus ??
        (() => {
          innerRef.current.dataset.focus = 'focus';
        }),
      blur:
        handlers?.onBlur ??
        (() => {
          delete innerRef.current.dataset.focus;
        }),
      input: ((hostEvent) => {
        handlers?.onInput?.(hostEvent);
      }),
    };
  }, [handlers, innerRef]);

  const removeRemoteControl = useCallback(
    (element, permanent = false) => {
      if (hasReflection.current) {
        removeControl({ element }, permanent);
        hasReflection.current = false;
      }
    },
    [removeControl],
  );

  const createRemoteControl = useCallback(
    (description) => {
      removeRemoteControl(description);

      createControl({
        element: {
          ...description,
          element_position: position.current,
        },
        events: eventHandlers,
      });
      hasReflection.current = true;
    },
    [createControl, eventHandlers, removeRemoteControl],
  );

  const isPositionChanged = useCallback((newPosition) => {
    const hasDiff = [
      newPosition?.top !== position.current?.top,
      newPosition?.left !== position.current?.left,
      newPosition?.width !== position.current?.width,
      newPosition?.height !== position.current?.height,
    ];
    return hasDiff.some(Boolean);
  }, []);

  useEffect(() => {
    if (!innerRef.current) {
      return;
    }

    const instance = innerRef.current;
    const id = instance.id;
    const description = {
      element_id: id,
      element_type: getElementTypeAlias(instance.tagName),
      widget_type: 'proxy_button',
      attributes: (attributes ?? []).reduce((acc, attr) => {
        return Object.assign(acc, {[attr]: instance[attr]});
      }, {}),
      zone_id,
    };

    if (zone_id === undefined) {
      console.log(`Control ${id} out of zone, omit register`);
      return;
    }

    console.log(`register element ${id} in ${zone_id}`);

    const unregister = registerElement(instance, (newPosition) => {
      console.log('on new position');

      if (isPositionChanged(newPosition)) {
        console.log('  create element');
        position.current = newPosition;
        createRemoteControl(description);
      }
    });

    const subscription = API.hostApp.message$
      .pipe(
        filter(
          /**
           * @param {Object} payload
           * @param {string} payload.element_id
           * @return {boolean}
           *
           * @todo: This filter rely on deprecated host app message format
           */
          ({ element_id }) => description.element_id === element_id),
        // well enough for the moment,
        // but will cause a problem for hook applied to emoji buttons,
        // where repeated clicks almost possible and allowed
        // distinctUntilChanged((prev, curr) => {
        //   return prev.element_id === curr.element_id && prev.event_type === curr.event_type;
        // })

        // Use throttle instead distinct, also fails due to mobile over, click received almost immediately
        // throttleTime(250),
      )
      .subscribe(({ payload }) => {
        const {
          event = {},
          event_type,
        } = payload;
        const {
          type = event_type,
          ...rest
        } = event;

        const innerEvent = new CustomEvent(`proxy-${type}`, {
          bubbles: true,
          detail: rest,
          cancelable: true,
        });
        innerRef.current.dispatchEvent(innerEvent);

        try {
          eventHandlers[type]?.(event);
        } catch (err) {
          console.error(
            `Error on event ${type} for ${description.event_id}`,
            err,
          );
        }
      });

    return () => {
      console.log(`un-register element ${id} in ${zone_id}`);
      unregister();
      subscription.unsubscribe();
      removeRemoteControl(description, true);
    };
  }, [createRemoteControl, innerRef, isPositionChanged, registerElement, removeRemoteControl, zone_id, eventHandlers, API.hostApp.message$, attributes]);

  return innerRef;
}
